import React, { useRef, useState } from 'react';
import { ImagePlus, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Posture image uploader.
 * - Accepts a single image (jpeg/png/webp/heic*)
 * - Resizes client-side to max 1600px (longest side), JPEG quality 0.85
 * - Returns a base64 data URL via onChange
 * - Typical resulting size: 150–500KB → safely fits in MongoDB doc
 */
export default function PostureImageUpload({ value, onChange, max = 1600, quality = 0.85 }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('이미지 파일만 업로드 가능합니다');
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeImage(file, max, quality);
      onChange?.(dataUrl);
    } catch (e) {
      console.error(e);
      toast.error('이미지 처리에 실패했습니다');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="posture-image-upload">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid="posture-file-input"
      />
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt="체형 사진"
            className="max-h-72 rounded-lg border border-gray-200 object-contain bg-gray-50"
            data-testid="posture-preview"
          />
          <button
            type="button"
            onClick={() => onChange?.(null)}
            className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 shadow hover:bg-red-50"
            aria-label="이미지 제거"
            data-testid="btn-remove-posture-image"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="w-full max-w-xs h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-500 hover:border-orange-400 hover:text-orange-500 transition-colors"
          data-testid="btn-upload-posture-image"
        >
          {busy ? (
            <Loader2 className="w-6 h-6 animate-spin" />
          ) : (
            <>
              <ImagePlus className="w-6 h-6 mb-1" />
              <span className="text-sm">체형 이미지 업로드</span>
              <span className="text-xs text-gray-400 mt-0.5">JPG · PNG · 최대 약 5MB</span>
            </>
          )}
        </button>
      )}
      {value && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            다른 이미지로 교체
          </Button>
        </div>
      )}
    </div>
  );
}

/** Resize an image File to a max dimension and return a JPEG base64 data URL. */
function resizeImage(file, maxDim, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        try {
          let { width, height } = img;
          const longest = Math.max(width, height);
          if (longest > maxDim) {
            const ratio = maxDim / longest;
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
